import { Injectable, inject } from '@angular/core';
import { firstValueFrom, map } from 'rxjs';
import {
    Action,
    IApiRequest,
    ICustomCodeReference,
    ICustomCodeResponse,
    TriggerEvent,
} from '../components/ui-builder/shared/interfaces/ac-triggers-list.interface';
import { AlphaHttpClient } from './alpha-http-client';
import { ProjectService } from './project.service';

@Injectable({ providedIn: 'root' })
export class CustomCodeService {
    private httpClient = inject(AlphaHttpClient);
    private projectService = inject(ProjectService);

    private domainUrl = {
        type: 'environment',
        value: {
            __alpha_env__: 'ALPHA_UI_CONFIG_SERVICE_URL',
        },
    };

    changedCustomCodes: { [key: string]: any } = {};
    customCodes: { [key: string]: any } = {};
    apiCustomCodes: Record<string, boolean> = {};
    deletedCustomCodes: string[] = [];

    private _customCodesReady: Promise<void> = Promise.resolve();

    /** Resolves when the latest `fetchAllCustomCode` HTTP call completes. */
    get customCodesReady(): Promise<void> {
        return this._customCodesReady;
    }

    saveAllCustomCode() {
        const customCodeArray = Object.keys(this.changedCustomCodes)
            .map((codeId) => ({
                codeId,
                code: this.changedCustomCodes[codeId],
                allowEmptyCustomCode: !!this.apiCustomCodes[codeId],
            }))
            .filter((item) => item.code || this.apiCustomCodes[item.codeId]);

        // Check if the array is empty
        if (customCodeArray.length === 0 && this.deletedCustomCodes.length === 0) {
            return;
        }

        const project = this.projectService.fetchCurrentProject();

        return this.httpClient
            .post<ICustomCodeResponse[]>(this.domainUrl, `/custom-code/save-all`, {
                customCodes: customCodeArray,
                projectId: project.projectId,
                deletedCustomCodes: this.deletedCustomCodes,
            })
            .pipe(
                map((savedData) => {
                    savedData.forEach((codeConfig) => {
                        this.customCodes[codeConfig.codeId] = codeConfig.code;
                    });
                    this.changedCustomCodes = {};
                    this.deletedCustomCodes = [];
                    return savedData;
                }),
            );
    }

    fetchCustomCode(codeId: string) {
        const project = this.projectService.fetchCurrentProject();
        return this.httpClient.post(this.domainUrl, `/custom-code/fetch`, {
            projectId: project.projectId,
            codeId: codeId,
        });
    }

    fetchAllCustomCode() {
        const project = this.projectService.fetchCurrentProject();
        const source$ = this.httpClient.post(this.domainUrl, `/custom-code/fetch-all`, {
            projectId: project.projectId,
        });

        this._customCodesReady = firstValueFrom(source$)
            .then((data) => {
                if (Array.isArray(data)) {
                    data.forEach((code: any) => {
                        this.customCodes[code.codeId] = code.code;
                    });
                    console.log('Pages Components:: this.customCodes', this.customCodes);
                }
            })
            .catch((err) => {
                console.error('Failed to fetch custom codes', err);
            });
    }

    updateCustomCodeIds(e: IEntitiesWithOutputs) {
        this.updateCustomCodeInTriggerEvents(e.outputs?.events || []);
        return e;
    }

    updateCustomCodeInTriggerEvents(t: TriggerEvent[]) {
        t.forEach((event) => {
            this.updateTriggersCustomCodes(event.triggers);
        });
        return t;
    }

    private updateTriggersCustomCodes(triggers: Action[]) {
        this.updateInCustomCodeTriggers(triggers as Action[]);
        this.updateInAPIRequestTrigger(triggers as IApiRequest[]);
    }

    updateInCustomCodeTriggers(triggerActions: Action[]) {
        triggerActions?.forEach((trigger: any) => {
            if (trigger?.actionName == 'Custom Code') {
                this.updateCustomCodeRef(trigger.config, trigger.value);
            }
        });
        return triggerActions;
    }

    updateInAPIRequestTrigger(triggers: IApiRequest[]) {
        triggers?.forEach((trigger) => {
            if (trigger?.actionName == 'API Request') {
                const beforeCustomCodeId = trigger.config?.before?.id || '';
                const afterCustomCodeId = trigger.config?.after?.id || '';
                trigger.config.before = this.updateCustomCodeRef(
                    trigger.config.before,
                    beforeCustomCodeId ? this.customCodes[beforeCustomCodeId] : '',
                );
                trigger.config.after = this.updateCustomCodeRef(
                    trigger.config.after,
                    afterCustomCodeId ? this.customCodes[trigger.config.after.id] : '',
                );
            }
        });
        return triggers;
    }

    private updateCustomCodeRef(customCodeRef: ICustomCodeReference, customCode = '') {
        const newId = window.crypto.randomUUID();
        customCodeRef.id = newId;
        customCodeRef.name = newId;
        this.changedCustomCodes[newId] = customCode;
        this.customCodes[newId] = customCode;
        return customCodeRef;
    }
}

export interface IEntitiesWithOutputs {
    outputs: {
        events: Array<TriggerEvent>;
    };
}

export interface IEventsWithOutputs {
    events: Array<TriggerEvent>;
}
